import React, { useRef, useEffect, useState } from 'react';
import SignaturePad from 'signature_pad';
import { RotateCcw, Check } from 'lucide-react';

interface SignatureCaptureProps {
  name: string;
  onSignatureChange: (signature: string) => void;
  signature?: string;
}

export function SignatureCapture({ name, onSignatureChange, signature }: SignatureCaptureProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    if (canvasRef.current) {
      signaturePadRef.current = new SignaturePad(canvasRef.current, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)',
        velocityFilterWeight: 0.7,
        minWidth: 0.5,
        maxWidth: 2.5,
        throttle: 16,
        minDistance: 5,
      });

      const handleEnd = () => {
        if (signaturePadRef.current) {
          const dataURL = signaturePadRef.current.toDataURL();
          setIsEmpty(signaturePadRef.current.isEmpty());
          onSignatureChange(dataURL);
        }
      };

      signaturePadRef.current.addEventListener('endStroke', handleEnd);

      // Load existing signature if provided
      if (signature && !isEmpty) {
        signaturePadRef.current.fromDataURL(signature);
        setIsEmpty(false);
      }

      // Handle canvas resize
      const resizeCanvas = () => {
        if (canvasRef.current && signaturePadRef.current) {
          const canvas = canvasRef.current;
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          const rect = canvas.getBoundingClientRect();
          
          canvas.width = rect.width * ratio;
          canvas.height = rect.height * ratio;
          canvas.getContext('2d')!.scale(ratio, ratio);
          
          signaturePadRef.current.clear();
          if (signature) {
            signaturePadRef.current.fromDataURL(signature);
          }
        }
      };

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      return () => {
        window.removeEventListener('resize', resizeCanvas);
        if (signaturePadRef.current) {
          signaturePadRef.current.off();
        }
      };
    }
  }, [signature, onSignatureChange]);

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setIsEmpty(true);
      onSignatureChange('');
    }
  };

  return (
    <div className="border border-gray-300 rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-medium text-gray-900">
            Signature for {name}
          </h4>
          <p className="text-sm text-gray-600">
            Please sign in the box below
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {!isEmpty && (
            <div className="flex items-center gap-1 text-green-600 text-sm">
              <Check className="w-4 h-4" />
              <span>Signed</span>
            </div>
          )}
          <button
            type="button"
            onClick={clearSignature}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Clear
          </button>
        </div>
      </div>

      <div className="relative">
        <canvas
          ref={canvasRef}
          className="w-full h-32 border border-gray-200 rounded cursor-crosshair bg-gray-50"
          style={{ touchAction: 'none' }}
        />
        
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-gray-400 text-sm">
              Sign here
            </span>
          </div>
        )}
      </div>
    </div>
  );
}